/**
 * Bulk Actions Bar Component
 * Floating action bar when items are selected. Reused for locations (Step 1/2/3) and users (Invite Users).
 * Single-row layout in both modes: count | separator | primary action | separator | close (X).
 *
 * Two modes:
 * - CRUD mode: Count + Delete (red) + Close. Use countLabel e.g. "users" for "3 users selected".
 * - Standards mode (Step 3): Count + Set Cleaning Standards (celeste) + Close.
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Trash2, X, Sparkles } from 'lucide-react';

export const BulkActionsBar = ({
  selectedCount,
  onDelete,
  onSetStandards,
  onCopyFrom,
  onClear,
  isDeleting = false,
  mode = 'crud',
  countLabel = '',
}) => {
  if (selectedCount === 0) return null;

  const countText =
    countLabel.trim() !== ''
      ? `${selectedCount} ${countLabel} selected`
      : `${selectedCount} selected`;

  // Standards Mode (Step 3) - Same single-row layout as CRUD mode; primary action in celeste (not red)
  if (mode === 'standards') {
    const standardsCountText = `${selectedCount} area${selectedCount !== 1 ? 's' : ''} selected`;
    return (
      <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-50 animate-in slide-in-from-bottom-4 duration-300">
        <div className="bg-[#35353a] text-white rounded-[12px] shadow-[0px_4px_20px_0px_rgba(0,0,0,0.3)] px-6 py-4 flex items-center gap-6">
          <p
            className="font-['Roboto:Medium',sans-serif] font-medium text-[16px]"
            style={{ fontVariationSettings: "'wdth' 100" }}
          >
            {standardsCountText}
          </p>

          <div className="w-[1px] h-6 bg-white/20" />

          {onSetStandards && (
            <button
              onClick={onSetStandards}
              className="flex items-center gap-2 hover:bg-white/10 px-4 py-2 rounded-lg transition-colors group"
              type="button"
            >
              <Sparkles className="w-[18px] h-[18px] text-[#61b8e7] group-hover:scale-110 transition-transform" />
              <span
                className="font-['Roboto:Medium',sans-serif] font-medium text-[15px] text-[#61b8e7]"
                style={{ fontVariationSettings: "'wdth' 100" }}
              >
                Set Cleaning Standards
              </span>
            </button>
          )}

          <div className="w-[1px] h-6 bg-white/20" />

          <button
            onClick={onClear}
            className="flex items-center justify-center hover:bg-white/10 p-2 rounded-lg transition-colors"
            aria-label="Clear selection"
            type="button"
          >
            <X className="w-[18px] h-[18px]" strokeWidth={2.5} />
          </button>
        </div>
      </div>
    );
  }

  // CRUD Mode (Step 2) - Only count, Delete and Close (z-50 so it appears above WizardActionBar z-40)
  return (
    <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-50 animate-in slide-in-from-bottom-4 duration-300">
      <div className="bg-[#35353a] text-white rounded-[12px] shadow-[0px_4px_20px_0px_rgba(0,0,0,0.3)] px-6 py-4 flex items-center gap-6">
        {/* Count */}
        <p
          className="font-['Roboto:Medium',sans-serif] font-medium text-[16px]"
          style={{ fontVariationSettings: "'wdth' 100" }}
        >
          {countText}
        </p>

        <div className="w-[1px] h-6 bg-white/20" />

        {/* Delete */}
        {onDelete && (
          <button
            onClick={onDelete}
            className="flex items-center gap-2 hover:bg-white/10 px-4 py-2 rounded-lg transition-colors group"
            disabled={isDeleting}
            type="button"
          >
            <Trash2 className="w-[18px] h-[18px] text-[#fc2e20] group-hover:scale-110 transition-transform" />
            <span
              className="font-['Roboto:Medium',sans-serif] font-medium text-[15px] text-[#fc2e20]"
              style={{ fontVariationSettings: "'wdth' 100" }}
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </span>
          </button>
        )}

        <div className="w-[1px] h-6 bg-white/20" />

        {/* Close */}
        <button
          onClick={onClear}
          className="flex items-center justify-center hover:bg-white/10 p-2 rounded-lg transition-colors"
          aria-label="Clear selection"
          type="button"
        >
          <X className="w-[18px] h-[18px]" strokeWidth={2.5} />
        </button>
      </div>
    </div>
  );
};

BulkActionsBar.propTypes = {
  selectedCount: PropTypes.number.isRequired,
  onDelete: PropTypes.func,
  onSetStandards: PropTypes.func,
  onCopyFrom: PropTypes.func,
  onClear: PropTypes.func.isRequired,
  isDeleting: PropTypes.bool,
  mode: PropTypes.oneOf(['crud', 'standards']),
  countLabel: PropTypes.string,
};
